// app/src/components/3D/useSnapResolver.ts
import { useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback } from 'react';
import * as THREE from 'three';
import { activeSnapAtom, snapSettingsAtom } from '@/atoms/snapping.atom';
import { gatherCandidates, type SnapFeature } from '@/lib/snapping/candidates';
import { resolveSnap } from '@/lib/snapping/snapEngine';
import { SNAP_THRESHOLD_PX, type SnapResult, type Vec3 } from '@/lib/snapping/types';

/**
 * Returns a stable `resolve(cursorPx, surfaceHit)` function that:
 *  1. Gathers snap candidates from the visible features supplied by the caller
 *     via `getVisibleFeatures`.
 *  2. Finds the best snap within SNAP_THRESHOLD_PX pixels using the engine.
 *  3. Publishes the result to `activeSnapAtom` (drives the SnapIndicator overlay).
 *  4. Returns the resolved `SnapResult`.
 *
 * `getVisibleFeatures` is provided by the calling drawing tool. It should
 * return the subset of vector features currently in view — typically the
 * `nearbyFeatures` array from `GeoJson` (already filtered to ≤ 35 m from
 * the camera), mapped to `{ id, geometry }`.
 */
export function useSnapResolver(getVisibleFeatures: () => SnapFeature[]) {
  const { camera, size } = useThree();
  const settings = useAtomValue(snapSettingsAtom);
  const setActiveSnap = useSetAtom(activeSnapAtom);

  return useCallback(
    (cursorPx: [number, number], surfaceHit: Vec3 | null): SnapResult => {
      // Project a world point into canvas pixel space.
      // Returns null when the point is outside the camera clip volume.
      const projectToPixels = (p: Vec3): [number, number] | null => {
        const ndc = new THREE.Vector3(p[0], p[1], p[2]).project(camera);
        if (ndc.z < -1 || ndc.z > 1) return null;
        return [((ndc.x + 1) / 2) * size.width, ((1 - ndc.y) / 2) * size.height];
      };

      const result = resolveSnap({
        cursorPx,
        projectToPixels,
        candidates: gatherCandidates(getVisibleFeatures()),
        surfaceHit,
        settings,
        thresholdPx: SNAP_THRESHOLD_PX,
      });

      setActiveSnap(result);
      return result;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [camera, size, settings, setActiveSnap, getVisibleFeatures],
  );
}
